import React, { createContext, useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useApiAuth } from '../utils/api-auth'
import { useTransactionHistoryApi } from '../api/transaction-history-api'
import { TransactionResponse } from '../types'

interface TransactionHistoryStore {
  data: null | TransactionResponse[]
  isLoading: boolean
  hasError: boolean
  update: (options: Record<string, any>) => void
}

export interface TransactionHistoryContextProps {
  transactionHistoryState: TransactionHistoryStore
}

export const TransactionHistoryContext = createContext(
  {} as TransactionHistoryContextProps,
)

export const TransactionHistoryProvider: React.FC = ({ children }) => {
  const { guid, tokenFailure, apigeeToken } = useApiAuth()
  const { fetchTransactionHistory } = useTransactionHistoryApi()
  const { id } = useParams<{ id: string }>()
  const [readyToFetchTransactionHistory, setReadyToFetchTransactionHistory] =
    useState(false)

  const [transactionHistoryState, setState] = useState<TransactionHistoryStore>(
    {
      data: null,
      isLoading: false,
      hasError: false,
      // No need to test setState as it is react built in function
      update: /* istanbul ignore next */ (options): void => {
        setState(prevState => {
          return {
            ...prevState,
            ...options,
          }
        })
      },
    },
  )

  const getTransactionHistory = async (): Promise<void> => {
    const { update } = transactionHistoryState
    update({ isLoading: true, hasError: false, data: [] })

    try {
      const response = await fetchTransactionHistory(
        JSON.stringify({
          accountNumberPvtEncrypt: id,
          recordsToPull: 20,
        }),
      )
      const data = response.searchResults
      /* istanbul ignore next */ // TODO - add coverage
      update({ data: data.flat() })
    } catch (error) {
      update({
        hasError: true,
      })
    } finally {
      update({ isLoading: false })
    }
  }

  // reset on accountId change. Don't reset readyToFetchTransactionHistory
  // because those conditions will already be met after the first load
  useEffect(() => {
    transactionHistoryState.update({
      data: null,
      isLoading: false,
      hasError: false,
    })
  }, [id])

  // does first fetch after tokens are ready
  useEffect(() => {
    if (readyToFetchTransactionHistory) {
      getTransactionHistory()
    }
  }, [readyToFetchTransactionHistory])

  useEffect(() => {
    /* istanbul ignore next */ // TODO - add coverage
    if (tokenFailure) {
      transactionHistoryState.update({ isLoading: false, hasError: true })
    }
  }, [tokenFailure])

  useEffect(() => {
    /* istanbul ignore next */
    if (!readyToFetchTransactionHistory) {
      if (guid && apigeeToken) {
        setReadyToFetchTransactionHistory(true)
      }
    }
  }, [guid, apigeeToken])

  return (
    <TransactionHistoryContext.Provider value={{ transactionHistoryState }}>
      {children}
    </TransactionHistoryContext.Provider>
  )
}

export const useTransactionHistoryContext =
  /* istanbul ignore next */ // TODO - add coverage
  (): TransactionHistoryContextProps => {
    const context = useContext(TransactionHistoryContext)
    if (context === undefined) {
      throw new Error(
        'useTransactionHistoryContext must be used within TransactionHistoryProvider',
      )
    }
    return context
  }
